import React, { useEffect, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { message } from 'antd';
import { getLeafletIcon, getIconDef } from './AnnotationIcons';
import { mapAnnotationService } from '@iso/services';
import { StyleToggle, escapeHtml } from './AnnotationShared';

const COLORS = ['#EF4444', '#EAB308', '#F97316', '#16A34A', '#3B82F6', '#8B5CF6', '#374151'];

const makeTextIcon = (label, color = '#374151', fontSize = 40, borderStyle = 'solid') => {
  const px = Math.round(12 + ((fontSize - 10) / 70) * 12);
  const borderCss = borderStyle === 'dotted' ? 'dotted' : borderStyle === 'dashed' ? 'dashed' : 'solid';
  return L.divIcon({
    html: `<div style="background:white;border:1.5px ${borderCss} #374151;border-radius:4px;padding:4px 10px;font-size:${px}px;font-weight:600;color:${escapeHtml(color)};white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.18);cursor:pointer;display:inline-block">${escapeHtml(label) || 'Label'}</div>`,
    className: '',
    iconSize: [200, 36],
    iconAnchor: [0, 18],
  });
};


// ─── AnnotationItem ───────────────────────────────────────────────────────────

const AnnotationItem = ({ annotation, onDelete, onUpdate, eraserMode }) => {
  const isText = annotation.annotation_type === 'text_label';
  const [labelInput,    setLabelInput]    = useState(annotation.label    || '');
  const [colorInput,    setColorInput]    = useState(annotation.color    || '#374151');
  const [sizeInput,     setSizeInput]     = useState(Number(annotation.font_size) || 40);
  const [rotationInput, setRotationInput] = useState(Number(annotation.rotation)  || 0);
  const [styleInput,    setStyleInput]    = useState(annotation.style    || 'solid');

  useEffect(() => {
    setLabelInput(annotation.label    || '');
    setColorInput(annotation.color    || '#374151');
    setSizeInput(Number(annotation.font_size) || 40);
    setRotationInput(Number(annotation.rotation)  || 0);
    setStyleInput(annotation.style    || 'solid');
  }, [annotation.label, annotation.color, annotation.font_size, annotation.rotation, annotation.style]);

  const def      = getIconDef(annotation.annotation_type);
  const typeName = def?.label || (isText ? 'Text Label' : annotation.annotation_type);
  const icon     = isText
    ? makeTextIcon(annotation.label, annotation.color || '#374151', Number(annotation.font_size) || 40, annotation.style || 'solid')
    : getLeafletIcon(annotation.annotation_type);

  const handleSave = () => {
    const updated = { ...annotation, label: labelInput, color: colorInput, font_size: sizeInput, rotation: rotationInput, style: styleInput };
    mapAnnotationService.storeOrUpdate({ map_annotation: updated }, annotation.id)
      .then(() => onUpdate(updated))
      .catch(err => { console.error('Failed to update annotation:', err); message.error('Failed to save changes'); });
  };

  const handleDelete = () => {
    mapAnnotationService.destroy(annotation.id)
      .then(() => onDelete(annotation.id))
      .catch(err => { console.error('Failed to delete annotation:', err); message.error('Failed to delete annotation'); });
  };

  if (eraserMode) {
    return (
      <Marker position={[parseFloat(annotation.lat), parseFloat(annotation.lng)]} icon={icon} onClick={handleDelete} />
    );
  }

  return (
    <Marker
      position={[parseFloat(annotation.lat), parseFloat(annotation.lng)]}
      icon={icon}
      zIndexOffset={isText ? 500 : 0}
    >
      <Popup minWidth={260}>
        <div style={{ minWidth: 250 }}>

          {/* Header */}
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: '#111827' }}>
            Properties
          </div>

          {/* Type */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, fontWeight: 500 }}>Type</div>
            <div style={{ fontSize: 14, color: '#3B82F6' }}>{typeName}</div>
          </div>

          {/* Label */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>Label</div>
            <input
              value={labelInput}
              onChange={e => setLabelInput(e.target.value)}
              placeholder="Enter label…"
              style={{
                width: '100%', padding: '9px 12px',
                border: '1px solid #f3f4f6', borderRadius: 10,
                fontSize: 14, background: '#f9fafb', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Text-label-only controls */}
          {isText && (
            <>
              {/* Size */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>
                  Size: {sizeInput}px
                </div>
                <input type="range" min={10} max={80} value={sizeInput}
                  onChange={e => setSizeInput(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#111827' }}
                />
              </div>

              {/* Border Style */}
              <StyleToggle value={styleInput} onChange={setStyleInput} />

              {/* Color */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Color</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {COLORS.map(hex => (
                    <button key={hex} onClick={() => setColorInput(hex)} style={{
                      width: 36, height: 36, borderRadius: 8, background: hex,
                      border: colorInput === hex ? '3px solid #111827' : '2px solid rgba(0,0,0,0.12)',
                      cursor: 'pointer', padding: 0,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Rotation — all types */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#1f2937', marginBottom: 5 }}>
              Rotation: {rotationInput}°
            </div>
            <input type="range" min={0} max={359} value={rotationInput}
              onChange={e => setRotationInput(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#111827' }}
            />
          </div>

          {/* Save */}
          <button onClick={handleSave} style={{
            width: '100%', padding: '9px 0', marginBottom: 8,
            background: '#0d3825', color: 'white', border: 'none',
            borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Save Changes
          </button>

          {/* Delete */}
          <button onClick={handleDelete} style={{
            width: '100%', padding: '11px 0',
            background: '#DC2626', color: 'white', border: 'none',
            borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete Annotation
          </button>

        </div>
      </Popup>
    </Marker>
  );
};

// ─── AnnotationMarkers ────────────────────────────────────────────────────────

const AnnotationMarkers = ({ annotations, hiddenCategories, onDelete, onUpdate, eraserMode }) => (
  <>
    {annotations
      .filter(a => !hiddenCategories.includes(a.category))
      .map(annotation => (
        <AnnotationItem key={annotation.id} annotation={annotation}
          onDelete={onDelete} onUpdate={onUpdate} eraserMode={eraserMode} />
      ))}
  </>
);

export default AnnotationMarkers;
